/**
 * The prototype's stand-in for a rewarded ad (docs/MONETIZATION.md § 2):
 * clearly marked "Sample ad (prototype)", started only by the player, and
 * closable at any moment. After a short wait the player can collect the
 * cosmetic sticker. Registered with the ads service as its presenter.
 */
import { useEffect, useId, useState } from 'react';
import { useT } from '../i18n/index.ts';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';
import { Icon } from './Icon.tsx';
import './ui.css';

/** How long the sample "ad" runs before the reward can be collected. */
export const SAMPLE_AD_MS = 5000;
const TICK_MS = 100;

export interface RewardedAdModalProps {
  onFinish(outcome: 'completed' | 'skipped'): void;
}

export function RewardedAdModal({ onFinish }: RewardedAdModalProps) {
  const t = useT();
  const titleId = useId();
  const [elapsed, setElapsed] = useState(0);
  const done = elapsed >= SAMPLE_AD_MS;

  useEffect(() => {
    if (done) return;
    const timer = setInterval(() => setElapsed((e) => Math.min(SAMPLE_AD_MS, e + TICK_MS)), TICK_MS);
    return () => clearInterval(timer);
  }, [done]);

  const percent = Math.round((100 * elapsed) / SAMPLE_AD_MS);

  return (
    <Dialog onClose={() => onFinish('skipped')} labelledBy={titleId} className="ui-reward">
      <div className="ui-dialog__head">
        <span className="ui-ad__label">{t('common.adLabel')}</span>
        <h2 id={titleId} className="ui-dialog__title">
          {t('common.adSampleTitle')}
        </h2>
      </div>
      <div className="ui-reward__stage" aria-hidden="true">
        <Icon name="sticker" size="3.5rem" />
      </div>
      <p className="ui-dialog__text">{t('shell.rewardBody')}</p>
      <div
        className="ui-progress"
        role="progressbar"
        aria-label={t('shell.rewardProgress')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span className="ui-progress__fill" style={{ width: `${percent}%` }} />
      </div>
      <div className="ui-dialog__actions">
        <Button variant="ghost" onClick={() => onFinish('skipped')}>
          {t('common.close')}
        </Button>
        <Button variant="primary" icon="sparkle" disabled={!done} onClick={() => onFinish('completed')}>
          {t('shell.rewardCollect')}
        </Button>
      </div>
    </Dialog>
  );
}
